import type { PrizeId, WheelSegment } from './wheelTypes';

/**
 * Visual segment layout for VBB ŞANS ÇARXI.
 * Every segment is a positive AZN prize. Extra small-prize slices keep
 * common outcomes visually present without adding empty/lose wedges.
 * Weights below are DISPLAY / DEMO ONLY — live rewards stay server-side.
 */
export const WHEEL_SEGMENTS: WheelSegment[] = [
  {
    id: 'azn5-1',
    prizeId: 'azn5',
    label: '5 AZN',
    amountAzn: 5,
    fill: '#ff6a00',
    fillDeep: '#c94400',
    highlight: '#ffb347',
    text: '#fff4b8',
    premium: false,
  },
  {
    id: 'azn10-1',
    prizeId: 'azn10',
    label: '10 AZN',
    amountAzn: 10,
    fill: '#ffd400',
    fillDeep: '#e0a800',
    highlight: '#fff36a',
    text: '#9a0048',
    premium: false,
  },
  {
    id: 'azn5-2',
    prizeId: 'azn5',
    label: '5 AZN',
    amountAzn: 5,
    fill: '#ff2f86',
    fillDeep: '#c0145c',
    highlight: '#ff8ab8',
    text: '#fff4b8',
    premium: false,
  },
  {
    id: 'azn20-1',
    prizeId: 'azn20',
    label: '20 AZN',
    amountAzn: 20,
    fill: '#ffb347',
    fillDeep: '#e07800',
    highlight: '#ffe08a',
    text: '#9a0048',
    premium: false,
  },
  {
    id: 'azn5-3',
    prizeId: 'azn5',
    label: '5 AZN',
    amountAzn: 5,
    fill: '#ff8500',
    fillDeep: '#d45c00',
    highlight: '#ffc266',
    text: '#fff4b8',
    premium: false,
  },
  {
    id: 'azn50-1',
    prizeId: 'azn50',
    label: '50 AZN',
    amountAzn: 50,
    fill: '#ffcc00',
    fillDeep: '#d49800',
    highlight: '#ffe86a',
    text: '#9a0048',
    premium: true,
  },
  {
    id: 'azn10-2',
    prizeId: 'azn10',
    label: '10 AZN',
    amountAzn: 10,
    fill: '#ff3d9a',
    fillDeep: '#c4186e',
    highlight: '#ff93c8',
    text: '#fff4b8',
    premium: false,
  },
  {
    id: 'azn100-1',
    prizeId: 'azn100',
    label: '100 AZN',
    amountAzn: 100,
    fill: '#ff7a00',
    fillDeep: '#c44e00',
    highlight: '#ffb14a',
    text: '#fff4b8',
    premium: true,
  },
  {
    id: 'azn5-4',
    prizeId: 'azn5',
    label: '5 AZN',
    amountAzn: 5,
    fill: '#ffc44d',
    fillDeep: '#e08a00',
    highlight: '#ffe89a',
    text: '#9a0048',
    premium: false,
  },
  {
    id: 'azn200-1',
    prizeId: 'azn200',
    label: '200 AZN',
    amountAzn: 200,
    fill: '#ffc400',
    fillDeep: '#c98e00',
    highlight: '#ffe45c',
    text: '#9a0048',
    premium: true,
  },
  {
    id: 'azn20-2',
    prizeId: 'azn20',
    label: '20 AZN',
    amountAzn: 20,
    fill: '#ff1f78',
    fillDeep: '#b81050',
    highlight: '#ff7eac',
    text: '#fff4b8',
    premium: false,
  },
  {
    id: 'azn500-1',
    prizeId: 'azn500',
    label: '500 AZN',
    amountAzn: 500,
    fill: '#ff9d00',
    fillDeep: '#c66c00',
    highlight: '#ffd24a',
    text: '#fff4b8',
    premium: true,
  },
];

export const SEGMENT_COUNT = WHEEL_SEGMENTS.length;
export const SEGMENT_SLICE = 360 / SEGMENT_COUNT;
export const MIN_PRIZE_AZN = 5;
export const ALLOWED_PRIZE_AZN = [5, 10, 20, 50, 100, 200, 500] as const;

/** Approved no-lose demo weights in basis points (sum = 10000). Not live credits. */
export const DEMO_PRIZE_WEIGHTS: { prizeId: PrizeId; weight: number }[] = [
  { prizeId: 'azn5', weight: 7000 },
  { prizeId: 'azn10', weight: 2000 },
  { prizeId: 'azn20', weight: 650 },
  { prizeId: 'azn50', weight: 220 },
  { prizeId: 'azn100', weight: 80 },
  { prizeId: 'azn200', weight: 40 },
  { prizeId: 'azn500', weight: 10 },
];

export const PRIZE_AZN: Record<PrizeId, number> = {
  azn5: 5,
  azn10: 10,
  azn20: 20,
  azn50: 50,
  azn100: 100,
  azn200: 200,
  azn500: 500,
};

export const AZN_TO_MINOR = 100;

export const MUTE_STORAGE_KEY = 'vbbplay.wheel.muted';

export function sanitizePrizeAmount(value: number): number {
  return (ALLOWED_PRIZE_AZN as readonly number[]).includes(value) ? value : MIN_PRIZE_AZN;
}

export function indexesForPrize(prizeId: PrizeId): number[] {
  return WHEEL_SEGMENTS.map((segment, index) => (segment.prizeId === prizeId ? index : -1)).filter(
    (index) => index >= 0,
  );
}

export function pickSegmentIndex(prizeId: PrizeId): number {
  const indexes = indexesForPrize(prizeId);
  if (indexes.length === 0) return 0;
  return indexes[Math.floor(Math.random() * indexes.length)] ?? 0;
}
